import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight, Sparkles, Zap, Shield, Star, CheckCircle2,
  Wrench, Truck, Hammer, Tv, Clock, BadgeCheck, MapPin,
  Lock, RotateCcw,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { SafetyModal } from "@/components/site/SafetyModal";
import { RealPhoto } from "@/components/site/RealPhoto";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/funnel-track";
import { topRatedQueryOptions, type TopRatedHelper } from "@/lib/top-rated";
import { TrustScore } from "@/components/site/TrustScore";
import heroImg from "../assets/hero-pro.jpg";
import movingImg from "../assets/cat-moving.jpg";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Peopels — Verified local professionals across Greater London" },
      { name: "description", content: "Need help? We've got the right peopels. Verified local professionals for handyman services, furniture assembly, TV mounting and moving help across Greater London." },
      { property: "og:title", content: "Peopels — Verified local professionals across Greater London" },
      { property: "og:description", content: "Need help? We've got the right peopels. Verified local professionals across Greater London." },
      { property: "og:url", content: "/" },
    ],
    links: [
      { rel: "canonical", href: "/" },
      { rel: "preload", as: "image", href: heroImg, fetchPriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Peopels",
          url: "/",
          slogan: "Hire trusted local Helpers across Greater London",
          foundingDate: "2025",
          areaServed: { "@type": "AdministrativeArea", name: "Greater London" },
          description:
            "Peopels is an early-stage marketplace for trusted local professionals across Greater London. Our goal is to become one of the most trusted local service platforms in the UK.",
          knowsAbout: ["Handyman", "Furniture Assembly", "TV Mounting", "Moving Help"],
          subjectOf: {
            "@type": "CreativeWork",
            name: "Our Journey",
            about:
              "Company vision and aspirational roadmap. Not press coverage or endorsements.",
            text: "Phase 1: Launch and grow across Greater London. Phase 2: Expand into major UK cities. Phase 3: Become a recognised leader in local services through customer experience, quality and trust.",
          },
        }),
      },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(topRatedQueryOptions).catch(() => []),
  component: Home,
});

// Hero bento — the four parent services with curated imagery.
const categories = [
  { slug: "handyman", name: "Handyman", icon: Wrench, from: 30 },
  { slug: "tv-mounting", name: "TV Mounting", icon: Tv, from: 35 },
  { slug: "furniture-assembly", name: "Furniture Assembly", icon: Hammer, from: 25 },
  { slug: "moving", name: "Moving Help", icon: Truck, from: 25 },
] as const;

// Full Peopels service catalog for the "Browse every service" strip.
const ALL_SERVICE_LINKS: { slug: string; name: string; from: number }[] = [
  { slug: "handyman", name: "Handyman", from: 30 },
  { slug: "furniture-assembly", name: "Furniture Assembly", from: 25 },
  { slug: "tv-mounting", name: "TV Mounting", from: 35 },
  { slug: "moving", name: "Moving Help", from: 25 },
  { slug: "furniture-removal", name: "Furniture Removal", from: 30 },
  { slug: "heavy-lifting", name: "Heavy Lifting", from: 25 },
  { slug: "ikea-assembly", name: "IKEA Assembly", from: 25 },
  { slug: "furniture-disassembly", name: "Furniture Disassembly", from: 25 },
  { slug: "shelf-mounting", name: "Shelf Mounting", from: 30 },
  { slug: "picture-hanging", name: "Picture Hanging", from: 25 },
  { slug: "curtain-blind-installation", name: "Curtains & Blinds", from: 30 },
  { slug: "home-repairs", name: "Home Repairs", from: 30 },
  { slug: "appliance-installation", name: "Appliance Install", from: 35 },
  { slug: "door-repair", name: "Door Repair", from: 35 },
  { slug: "drywall-repair", name: "Drywall Repair", from: 35 },
];

// Fire-once impression tracker — emits a funnel_event when the element first scrolls into view.
function useImpression(event: Parameters<typeof track>[0], props: Record<string, unknown> = {}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const fired = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || fired.current) return;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && !fired.current) {
          fired.current = true;
          void track(event, props);
          io.disconnect();
        }
      }
    }, { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);
  return ref;
}

function Home() {
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });

  const { data: liveTopRated } = useQuery(topRatedQueryOptions);
  const topRatedRows: TopRatedHelper[] = (liveTopRated && liveTopRated.length > 0)
    ? liveTopRated
    : [];

  const trustStripRef = useImpression("trust_strip_impression", { source: "hero" });
  const topRatedRef = useImpression("top_rated_impression", {
    source: "homepage",
    cache_size: topRatedRows.length,
    cached: topRatedRows.length > 0,
  });
  const journeyRef = useImpression("journey_impression", { source: "homepage" });
  const ambitionRef = useImpression("ambition_impression", { source: "homepage" });

  // Scroll-depth tracking within the Our Journey section (50% + 100% visible).
  const journeySectionRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = journeySectionRef.current;
    if (!el || typeof window === "undefined") return;
    const fired = { half: false, full: false };
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!fired.half && e.intersectionRatio >= 0.5) {
          fired.half = true;
          void track("journey_scroll_50", { source: "homepage" });
        }
        if (!fired.full && e.intersectionRatio >= 0.95) {
          fired.full = true;
          void track("journey_scroll_100", { source: "homepage" });
          io.disconnect();
        }
      }
    }, { threshold: [0.5, 0.95] });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const handleChipClick = (slug: string) => {
    void track("hero_cta_click", { source: "category_chip", category: slug });
  };



  const handleTopRatedClick = (helper: { helper_id?: string; name?: string; rank?: number }, index: number) => {
    void track("top_rated_click", { helper_id: helper.helper_id ?? null, name: helper.name ?? null, rank: helper.rank ?? index + 1, index });
  };




  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main id="main-content" className="flex-1">
        {/* HERO */}
        <section
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setMouse({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
          }}
          className="noise relative overflow-hidden bg-gradient-hero text-primary-foreground"
        >
          {/* aurora blobs */}
          <div aria-hidden className="pointer-events-none absolute -left-32 top-10 h-[420px] w-[420px] rounded-full bg-primary/30 blur-3xl animate-blob" />
          <div aria-hidden className="pointer-events-none absolute -right-24 top-40 h-[520px] w-[520px] rounded-full bg-fuchsia-500/20 blur-3xl animate-blob" style={{ animationDelay: "-6s" }} />
          <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.18]" style={{
            backgroundImage: "linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)",
          }} />

          <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:pb-28 lg:pt-24">
            <div className="space-y-7 lg:col-span-7">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium backdrop-blur">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                <span>Now live across Greater London</span>
              </span>

              <h1 className="font-display text-[2.4rem] font-extrabold leading-[1.05] tracking-[-0.03em] sm:text-5xl lg:text-6xl xl:text-7xl">
                <span className="text-white/95">Need help?</span>
                <br />
                <span className="text-gradient-hero">We've got the right peopels.</span>
              </h1>
              <p className="max-w-xl text-base text-white/75 sm:text-lg">
                Verified local professionals for handyman services, furniture assembly, TV mounting and moving help across London.
              </p>
              <p className="max-w-xl text-sm text-white/70">
                Post your job in minutes. Your card is securely authorized when booking, funds are only released once the work is completed.
              </p>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button
                  asChild
                  size="lg"
                  className="rounded-xl shadow-glow"
                  onClick={() => void track("hero_cta_click", { source: "hero_primary", target: "post_job" })}
                >
                  <Link to="/post-job">
                    Post a Job <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-xl border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white"
                  onClick={() => void track("hero_cta_click", { source: "hero_secondary", target: "become_helper" })}
                >
                  <Link to="/become-helper">Become a Professional</Link>
                </Button>
              </div>

              {/* Trust strip — opens "How we keep you safe" modal */}
              <div ref={trustStripRef}>
                <SafetyModal
                  source="hero_trust_strip"
                  trigger={
                    <button
                      type="button"
                      onClick={() => void track("trust_strip_click", { source: "hero_trust_strip" })}
                      aria-label="How we keep every booking safe"
                      className="group grid w-full grid-cols-2 gap-2 rounded-2xl border border-white/15 bg-white/5 p-3 text-left backdrop-blur transition hover:border-white/30 hover:bg-white/10 sm:grid-cols-4"
                    >
                      <div className="flex items-start gap-2.5">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-emerald-400/15 text-emerald-300"><BadgeCheck className="h-4 w-4" /></span>
                        <p className="text-xs font-semibold leading-snug text-white">Verified<br />Professionals</p>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-emerald-400/15 text-emerald-300"><Lock className="h-4 w-4" /></span>
                        <p className="text-xs font-semibold leading-snug text-white">Secure<br />Payments</p>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-emerald-400/15 text-emerald-300"><Star className="h-4 w-4" /></span>
                        <p className="text-xs font-semibold leading-snug text-white">Transparent<br />Reviews</p>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-emerald-400/15 text-emerald-300"><MapPin className="h-4 w-4" /></span>
                        <p className="text-xs font-semibold leading-snug text-white">Greater London<br />Focus</p>
                      </div>
                    </button>
                  }
                />
                <p className="mt-1.5 text-[11px] text-white/55">Tap to see how we keep every booking safe →</p>
              </div>

              {/* One-tap category chips */}
              <div className="flex flex-wrap gap-2 pt-1" aria-label="Popular services">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to="/post-job"
                    search={{ category: c.slug }}
                    onClick={() => handleChipClick(c.slug)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-medium text-white backdrop-blur transition hover:bg-white/20"
                  >
                    <c.icon className="h-3.5 w-3.5" aria-hidden /> {c.name}
                  </Link>
                ))}
              </div>

            </div>


            {/* Cinematic photo card */}
            <div className="relative lg:col-span-5">
              <div className="relative mx-auto max-w-md px-2 pb-6 sm:px-6 sm:pb-10 lg:px-10 lg:pb-12 lg:pt-6">
                <div
                  className="relative transition-transform duration-300 will-change-transform"
                  style={{
                    transform: `perspective(1200px) rotateY(${(mouse.x - 0.5) * 6}deg) rotateX(${(0.5 - mouse.y) * 6}deg)`,
                  }}
                >
                  <div className="absolute -inset-6 rounded-[2.2rem] bg-gradient-to-tr from-primary/40 to-fuchsia-500/30 blur-2xl" aria-hidden />
                  <div className="relative overflow-hidden rounded-[2rem] ring-1 ring-white/15 shadow-elevated">
                    <img
                      src={heroImg}
                      alt="Vetted Helper mounting a TV in a modern London apartment"
                      className="aspect-[4/5] w-full object-cover"
                      width={1080}
                      height={1350}
                      fetchPriority="high"
                    />
                    <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  </div>
                </div>

                {/* Floating trust cards — visible on sm+ to avoid clipping on small screens */}
                <div aria-hidden="false" className="pointer-events-none absolute inset-0 hidden sm:block">
                  {/* Card 1 — top left: booking confirmed */}
                  <div className="animate-float-slow pointer-events-auto absolute -left-4 top-6 w-[15rem] rounded-2xl border border-white/40 bg-white/80 p-3 text-foreground shadow-elevated backdrop-blur-xl lg:-left-10">
                    <div className="flex items-center gap-2.5">
                      <span className="relative grid h-9 w-9 shrink-0 place-items-center rounded-full bg-success/15 text-success">
                        <CheckCircle2 className="h-5 w-5" aria-hidden />
                        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-success ring-2 ring-white" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-success">Booking confirmed</p>
                        <p className="truncate text-sm font-semibold">James is on the way</p>
                        <p className="text-xs text-muted-foreground">Arrives today at 14:30</p>
                      </div>
                    </div>
                  </div>

                  {/* Card 2 — bottom right: verified pro */}
                  <div className="animate-float pointer-events-auto absolute -right-4 bottom-10 w-[16rem] rounded-2xl border border-white/40 bg-white/85 p-3 text-foreground shadow-elevated backdrop-blur-xl lg:-right-10">
                    <div className="flex items-center gap-2.5">
                      <span className="relative shrink-0">
                        <span className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-primary to-primary-glow text-sm font-bold text-primary-foreground">JM</span>
                        <span className="absolute -bottom-0.5 -right-0.5 grid h-4 w-4 place-items-center rounded-full bg-primary ring-2 ring-white">
                          <BadgeCheck className="h-3 w-3 text-primary-foreground" aria-hidden />
                        </span>
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="truncate text-sm font-semibold">Verified Professional</p>
                        </div>
                        <div className="flex items-center gap-1 text-amber-500" aria-label="Five star rating">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className="h-3 w-3 fill-current" aria-hidden />
                          ))}
                        </div>
                        <p className="truncate text-xs text-muted-foreground">TV Mounting Specialist</p>
                        <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Clock className="h-3 w-3" aria-hidden /> Typically responds in minutes
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Card 3 — bottom left: payment protection */}
                  <div className="animate-float-slow pointer-events-auto absolute -left-2 bottom-2 w-[14.5rem] rounded-2xl border border-white/40 bg-white/80 p-3 text-foreground shadow-elevated backdrop-blur-xl lg:-left-8 lg:bottom-24">
                    <div className="flex items-center gap-2.5">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                        <Lock className="h-4 w-4" aria-hidden />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">Protected payment</p>
                        <p className="truncate text-sm font-semibold">Funds securely held</p>
                        <p className="text-xs text-muted-foreground">Released after job completion</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile stacked trust cards — keeps the same trust signals without overflow */}
              <ul className="mt-5 grid grid-cols-1 gap-2 sm:hidden">
                <li className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/10 p-3 backdrop-blur-md">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-success/20 text-success">
                    <CheckCircle2 className="h-5 w-5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-white/80">Booking confirmed</p>
                    <p className="truncate text-sm font-semibold text-white">James is on the way · 14:30</p>
                  </div>
                </li>
                <li className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/10 p-3 backdrop-blur-md">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/15 text-white">
                    <BadgeCheck className="h-5 w-5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-white/80">Verified professional</p>
                    <p className="truncate text-sm font-semibold text-white">TV Mounting Specialist · ★★★★★</p>
                  </div>
                </li>
                <li className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/10 p-3 backdrop-blur-md">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/15 text-white">
                    <Lock className="h-5 w-5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-white/80">Protected payment</p>
                    <p className="truncate text-sm font-semibold text-white">Funds released after completion</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

        </section>

        {/* Brand message banner */}
        <section aria-label="Brand message" className="border-b border-border bg-card">
          <div className="mx-auto max-w-7xl px-4 py-6 text-center sm:px-6 lg:px-8">
            <p className="font-display text-lg italic tracking-tight text-foreground sm:text-xl">
              For the people, by the Peopels.
            </p>
          </div>
        </section>

        {/* All services strip */}
        <section aria-labelledby="all-services-heading" className="mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Services</p>
              <h2 id="all-services-heading" className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                Browse every Peopels service
              </h2>
            </div>
            <Link to="/categories" className="hidden text-sm font-semibold text-primary hover:underline sm:inline">
              See all →
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {ALL_SERVICE_LINKS.map((s) => (
              <Link
                key={s.slug}
                to="/services/$slug"
                params={{ slug: s.slug }}
                className="rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-card"
              >
                <p className="text-sm font-semibold">{s.name}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">From £{s.from}/hr</p>
              </Link>
            ))}
          </div>
        </section>


        {/* OUR JOURNEY */}
        <section
          ref={(node) => {
            journeyRef.current = node as HTMLDivElement | null;
            journeySectionRef.current = node;
          }}
          aria-labelledby="our-journey-heading"
          className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8"
        >
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Our Journey</p>
            <h2 id="our-journey-heading" className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
              Peopels is just getting started.
            </h2>
            <p className="mt-5 text-base text-muted-foreground sm:text-lg">
              We're building a modern marketplace for trusted local professionals across Greater London. Our goal is to become one of the most trusted local service platforms in the UK.
            </p>
          </div>

          <ol className="mt-12 grid gap-5 sm:grid-cols-3" aria-label="Company vision phases">
            {[
              {
                phase: "Phase 1",
                title: "Launch across Greater London",
                body: "Onboard quality professionals, deliver reliable service, earn customer trust one job at a time.",
                status: "In progress",
                tone: "bg-primary text-primary-foreground",
              },
              {
                phase: "Phase 2",
                title: "Expand into major UK cities",
                body: "Grow a trusted professional network beyond London while keeping the same quality bar.",
                status: "Next",
                tone: "bg-secondary text-secondary-foreground",
              },
              {
                phase: "Phase 3",
                title: "Become a recognised leader",
                body: "Earn industry recognition through customer experience, quality and trust, not marketing claims.",
                status: "Ambition",
                tone: "bg-muted text-foreground",
              },
            ].map((p) => (
              <li key={p.phase} className="relative rounded-3xl border border-border bg-card p-6 shadow-card">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">{p.phase}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${p.tone}`}>{p.status}</span>
                </div>
                <h3 className="mt-3 font-display text-xl font-bold tracking-tight">{p.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{p.body}</p>
              </li>
            ))}
          </ol>

          {/* Ambition */}
          <div
            ref={ambitionRef}
            aria-labelledby="our-ambition-heading"
            role="region"
            className="mt-12 overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-card to-muted/40 p-6 sm:p-10"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Our Ambition</p>
            <h3 id="our-ambition-heading" className="mt-2 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
              We are at the beginning of our journey.
            </h3>
            <div className="mt-3 space-y-2 text-sm text-muted-foreground sm:text-base">
              <p>Our goal is to build one of the most trusted local service platforms in the UK.</p>
              <p>Recognition is earned through quality, trust and customer experience, not through marketing claims.</p>
            </div>
          </div>
        </section>

        {/* BUILT FOR THE PEOPLE — values, not vanity metrics */}
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">What we stand for</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
              Built for the people. Powered by trusted professionals.
            </h2>
            <p className="mt-5 text-base text-muted-foreground sm:text-lg">
              Peopels is building a modern marketplace for trusted local professionals across Greater London. Our focus is simple: quality professionals, transparent reviews, secure payments and an exceptional customer experience.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { i: BadgeCheck, t: "Verified Professionals", d: "Every professional completes our verification process before joining the platform." },
              { i: Star,       t: "Quality First",          d: "We focus on trusted professionals and great customer experiences." },
              { i: Lock,       t: "Secure Payments",        d: "Payments are processed securely through trusted payment infrastructure." },
              { i: MapPin,     t: "Greater London Focus",   d: "We are starting in Greater London and building the foundation for something much bigger." },
            ].map((card) => (
              <div key={card.t} className="group rounded-3xl border border-border bg-card p-6 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-elevated">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-accent text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <card.i className="h-5 w-5" />
                </span>
                <p className="mt-4 font-semibold">{card.t}</p>
                <p className="mt-1 text-sm text-muted-foreground">{card.d}</p>
              </div>
            ))}
          </div>
        </section>


        {/* CATEGORIES — magazine style */}
        <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Services</p>
              <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">Whatever it is, we know someone.</h2>
            </div>
            <Link to="/categories" className="hidden text-sm font-semibold text-primary hover:underline sm:inline-flex">All categories →</Link>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-6 lg:grid-cols-12">
            {/* Featured large — Handyman */}
            <Link to="/post-job" search={{ category: "handyman" }} className="group relative col-span-6 overflow-hidden rounded-3xl lg:col-span-7 lg:row-span-2">
              <RealPhoto category="handyman" fallback={heroImg} alt="Vetted handyman fixing a fitting in a London home" width={800} height={1024} className="h-full min-h-[420px] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <span className="absolute left-5 top-5 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-secondary shadow-card">
                <Star className="h-3 w-3 fill-amber-500 text-amber-500" /> Most booked this week
              </span>
              <div className="absolute inset-x-0 bottom-0 p-6 text-white sm:p-8">
                <div className="flex items-center gap-2 text-xs text-white/80"><Wrench className="h-4 w-4" /> Handyman</div>
                <h3 className="mt-2 font-display text-2xl font-bold sm:text-3xl">One Helper, every odd job sorted.</h3>
                <p className="mt-2 max-w-md text-sm text-white/80">From £30/hr, with sameday availability across London.</p>
              </div>
            </Link>


            <Link to="/post-job" search={{ category: "tv-mounting" }} className="group relative col-span-3 overflow-hidden rounded-3xl lg:col-span-5">
              <RealPhoto category="tv-mounting" fallback={heroImg} alt="Helper mounting a flat-screen TV on a living-room wall" width={800} height={1024} className="h-full min-h-[200px] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                <div className="flex items-center gap-2 text-xs text-white/80"><Tv className="h-4 w-4" /> TV Mounting</div>
                <h3 className="mt-1 font-display text-lg font-bold">Wall-mounted, cables hidden, done.</h3>
              </div>
            </Link>

            <Link to="/post-job" search={{ category: "moving" }} className="group relative col-span-3 overflow-hidden rounded-3xl lg:col-span-5">
              <RealPhoto category="moving" fallback={movingImg} alt="Movers carrying furniture from a Victorian London townhouse" width={800} height={1024} className="h-full min-h-[200px] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                <div className="flex items-center gap-2 text-xs text-white/80"><Truck className="h-4 w-4" /> Moving Help</div>
                <h3 className="mt-1 font-display text-lg font-bold">Move without the stress.</h3>
              </div>
            </Link>
          </div>

          {/* compact category chips */}
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to="/post-job"
                search={{ category: c.slug }}
                className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-card transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-elevated"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-accent text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <c.icon className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{c.name}</p>
                  <p className="text-xs text-muted-foreground">from £{c.from}/hr</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* HOW IT WORKS — premium */}
        <section className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div aria-hidden className="absolute inset-x-0 top-0 -z-10 bg-gradient-mesh blur-3xl" />
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">How it works</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">Help, in three taps.</h2>
            <p className="mt-3 text-muted-foreground">Built to be the fastest way to get something done in London.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              { n: "01", t: "Describe your task", d: "Tell us what you need in plain English, our AI structures the rest.", i: Sparkles, chip: "Takes 30 seconds" },
              { n: "02", t: "Get matched", d: "Browse verified Helpers near you with transparent profiles and reviews.", i: Zap, chip: "Real reviews only" },
              { n: "03", t: "Book, chat, pay securely", d: "Payment held safely until the job is done. Rate your Helper after.", i: Shield, chip: "Money-back guarantee" },
            ].map((s) => (
              <div key={s.n} className="group relative overflow-hidden rounded-3xl border border-border bg-card p-7 shadow-card transition-all hover:-translate-y-1 hover:shadow-elevated">
                <div aria-hidden className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-gradient-to-br from-primary/20 to-fuchsia-500/10 blur-2xl transition-opacity group-hover:opacity-100" />
                <div className="flex items-center justify-between">
                  <span className="font-display text-5xl font-extrabold text-foreground/10">{s.n}</span>
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-accent text-primary">
                    <s.i className="h-5 w-5" />
                  </span>
                </div>
                <h3 className="mt-4 text-lg font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-accent px-2.5 py-1 text-[11px] font-semibold text-primary">
                  <CheckCircle2 className="h-3 w-3" /> {s.chip}
                </span>
              </div>
            ))}
          </div>

        </section>

        {/* TOP RATED — only renders when real cached data exists */}
        {topRatedRows.length > 0 && (
          <section className="bg-surface py-20">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div ref={topRatedRef} className="rounded-3xl border border-border bg-card p-6 shadow-card sm:p-8">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Top rated this week</p>
                    <h3 className="mt-1 font-display text-xl font-bold sm:text-2xl">Highest-scoring Helpers in London right now</h3>
                    <p className="mt-1 text-xs text-muted-foreground">Updated hourly from real reviews and completed jobs</p>
                  </div>
                  <Link to="/browse" className="text-sm font-semibold text-primary hover:underline">Browse all Helpers →</Link>
                </div>
                <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {topRatedRows.map((h, i) => (
                    <Link
                      key={h.helper_id}
                      to="/helpers/$helperId"
                      params={{ helperId: h.helper_id }}
                      onClick={() => handleTopRatedClick({ helper_id: h.helper_id, name: h.display_name, rank: h.rank }, i)}
                      className="group flex items-center gap-3 rounded-2xl border border-border bg-surface p-3 transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-card"
                    >
                      <div className="relative">
                        {h.avatar_url ? (
                          <img src={h.avatar_url} alt="" loading="lazy" width={48} height={48} className="h-12 w-12 rounded-full object-cover ring-2 ring-background" />
                        ) : (
                          <span className="grid h-12 w-12 place-items-center rounded-full bg-accent text-sm font-semibold text-primary ring-2 ring-background">
                            {h.display_name.slice(0,1)}
                          </span>
                        )}
                        <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white ring-2 ring-background" aria-label="ID verified">
                          <BadgeCheck className="h-3 w-3" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="truncate text-sm font-semibold">{h.display_name}</p>
                          {h.review_count > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-amber-600">
                              <Star className="h-3 w-3 fill-current" /> {Number(h.avg_rating).toFixed(2)}
                            </span>
                          )}
                        </div>
                        <p className="truncate text-xs text-muted-foreground">{h.headline ?? "Verified Helper"}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <TrustScore score={h.trust_score} size="sm" showLabel={false} />
                          <p className="truncate text-[11px] text-muted-foreground">
                            {h.area ?? "London"}
                            {h.review_count > 0 ? ` · ${h.review_count} review${h.review_count === 1 ? "" : "s"}` : ""}
                            {h.recent_jobs > 0 ? ` · ${h.recent_jobs} this week` : ""}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}



        {/* TRUST */}
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              { i: BadgeCheck, v: "ID-verified", d: "Every Helper passes identity & DBS checks before going live." },
              { i: Shield,    v: "Secure payments", d: "Funds held in escrow until the job is complete." },
              { i: Star,      v: "Rated & reviewed", d: "Real feedback from real customers, on every profile." },
              { i: Clock,     v: "London support 24/7", d: "Friendly help from a real human, whenever you need it." },
            ].map((t) => (
              <div key={t.v} className="group rounded-3xl border border-border bg-card p-6 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-elevated">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-accent text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <t.i className="h-5 w-5" />
                </span>
                <p className="mt-4 font-semibold">{t.v}</p>
                <p className="mt-1 text-sm text-muted-foreground">{t.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA — Helper recruiting with eligibility + timeline */}
        <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
          <div className="noise relative overflow-hidden rounded-[2rem] bg-gradient-hero p-8 text-primary-foreground shadow-elevated sm:p-12 lg:p-16">
            <div aria-hidden className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-primary/40 blur-3xl animate-blob" />
            <div aria-hidden className="pointer-events-none absolute -right-24 -bottom-24 h-80 w-80 rounded-full bg-fuchsia-500/30 blur-3xl animate-blob" style={{ animationDelay: "-4s" }} />

            <div className="relative grid gap-10 lg:grid-cols-2 lg:items-center">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">For Helpers</p>
                <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">Build your business with Peopels.</h2>
                <p className="mt-3 max-w-xl text-white/80">We're onboarding our founding group of verified professionals across Greater London. Keep 90% of what you charge. Free to join, secure payouts after every job.</p>

                <div className="mt-6 grid max-w-md grid-cols-3 gap-3">
                  <div className="rounded-2xl border border-white/15 bg-white/5 p-3 text-center">
                    <p className="font-display text-2xl font-extrabold">90%</p>
                    <p className="text-xs text-white/70">You keep</p>
                  </div>
                  <div className="rounded-2xl border border-white/15 bg-white/5 p-3 text-center">
                    <p className="font-display text-2xl font-extrabold">&lt; 5 min</p>
                    <p className="text-xs text-white/70">To sign up</p>
                  </div>
                  <div className="rounded-2xl border border-white/15 bg-white/5 p-3 text-center">
                    <p className="font-display text-2xl font-extrabold">Instant</p>
                    <p className="text-xs text-white/70">Payouts</p>
                  </div>
                </div>

                <div className="mt-7 flex flex-wrap gap-3">
                  <Button asChild size="lg" className="rounded-xl bg-white text-secondary shadow-elevated hover:bg-white/90">
                    <Link to="/become-helper">Start earning, it's free</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="rounded-xl border-white/30 bg-transparent text-white hover:bg-white/10">
                    <Link to="/how-it-works">How it works</Link>
                  </Button>
                </div>
                <p className="mt-4 text-xs text-white/60">No subscription, no hidden fees, and you can cancel any time.</p>
              </div>

              {/* Eligibility checklist + what happens next */}
              <div className="grid gap-4 sm:grid-cols-2 lg:gap-5">
                <div className="rounded-2xl border border-white/15 bg-white/5 p-5 backdrop-blur">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70">Eligibility</p>
                  <p className="mt-1 font-display text-lg font-bold">You qualify if you have…</p>
                  <ul className="mt-4 space-y-2.5 text-sm text-white/85">
                    {[
                      "Right to work in the UK",
                      "Government-issued photo ID",
                      "A UK bank account",
                      "A smartphone with a camera",
                      "Skill or experience in 1+ category",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl border border-white/15 bg-white/5 p-5 backdrop-blur">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70">What happens next</p>
                  <p className="mt-1 font-display text-lg font-bold">From signup to first job</p>
                  <ol className="mt-4 space-y-3 text-sm text-white/85">
                    {[
                      { n: "1", t: "Sign up", d: "Around 5 minutes to upload your ID and select services" },
                      { n: "2", t: "Get verified", d: "Usually under 24 hours" },
                      { n: "3", t: "Receive job offers", d: "Within 48 hours of approval" },
                      { n: "4", t: "Get paid", d: "Instantly after each job" },
                    ].map((step) => (
                      <li key={step.n} className="flex items-start gap-3">
                        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-white/15 text-[11px] font-bold text-white">{step.n}</span>
                        <div>
                          <p className="font-semibold">{step.t}</p>
                          <p className="text-xs text-white/60">{step.d}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* Sticky mobile CTA — high-intent conversion shortcut */}
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 p-3 shadow-elevated backdrop-blur md:hidden">
          <Button asChild size="lg" className="w-full rounded-xl shadow-glow">
            <Link to="/post-job">Post a job free — get matched with vetted Helpers</Link>
          </Button>
        </div>

      </main>
      <Footer />
    </div>
  );
}
